import React, {useState, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useSearchParams, useNavigate} from 'react-router-dom';

import styles from './Shop.module.scss';

import ShopOffers from "./ShopOffers";
import ShopReviewOrder from "./ShopReviewOrder";
import OfferPopupInfo from "../../components/OfferPopupInfo/OfferPopupInfo";
import Loading from "../../components/Loading/Loading";
import PopupInfo from "../../components/PopupInfo/PopupInfo";
import withRequestHandler from "../../HOC/withRequestHandler";
import {
    logUserOut,
    showGeneralError,
    startLoading,
    setSelectedOffer,
    clearPurchaseIntention,
    setPaymentProvider
} from "../../redux/rootReducer";

const DEFAULT_TIME_FOR_REFRESH_OFFERS = 900000;

const Shop = ({
    config,
    windowSize,
    makeRequest,
    isLoading,
    logout
}) => {
    let player = localStorage.getItem('shishiPlayerInfo');
    player = JSON.parse(player);

    const navigate = useNavigate();
    let isFirstPageView = useRef(true);
    let debounceTimeout = useRef(0);
    let debounceTimeoutFirstRender = useRef(0);
    let refreshAfter = useRef(DEFAULT_TIME_FOR_REFRESH_OFFERS);    // 15 minutes default
    let timePointToBeRefreshed = useRef(0);
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const { error, selectedOffer } = useSelector(state => state.rootReducer);
    const [offerPopupInfo, setOfferPopupInfo] = useState({});
    const [offersCategories, setOffersCategories] = useState([]);
    const [allOffers, setAllOffers] = useState([]);
    const [highlitedOffers, setHighlitedOffers] = useState([]);
    const [searchedOffers, setSearchedOffers] = useState([]);
    const [hasActiveSearch, setHasActiveSearch] = useState(false);
    const [promoBannerUrl, setPromoBannerUrl] = useState(null);

    const scheduleRefreshOffers = (offers) => {
        const offersWithTimers = [];
        offers.forEach(category => {
            category.packs.forEach(offer => {
                if (offer.endTime) {
                    offersWithTimers.push(offer);
                }
            });
        });

        if (offersWithTimers.length) {
            const offersTimes = offersWithTimers.map(offer => (offer.endTime * 1000) - Date.now());
            if (offersTimes.length) {
                refreshAfter.current = offersTimes.reduce((a, b) => a < b ? a : b);
            }
            if (refreshAfter.current > DEFAULT_TIME_FOR_REFRESH_OFFERS) {
                refreshAfter.current = DEFAULT_TIME_FOR_REFRESH_OFFERS;
            }
        }

        clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            timePointToBeRefreshed = refreshAfter.current + Date.now();
            getOffers();
        }, refreshAfter.current + 1000);
    }

    const getPromoBannerUrl = async () => {
        const data = await makeRequest('get', 'banner-link/shop');
        if (data && data.url && data.url.length > 0) {
            setPromoBannerUrl(data.url);
        }
    }

    const getOffers = async () => {
            const data = await makeRequest('post', 'loadProducts', {username: player.username, userId: player.userId});
            const highlightedOffers = [];

        if (data && data.adBaners && data.adBaners.length) {
            data.adBaners.forEach(banner => {
                banner.noOffer = true;
                banner.highlightedBannerMobile = banner.imageMobile;
                banner.highlightedBanner = banner.image;
                highlightedOffers.push(banner);
            });
        }

            if (data && data.categories && data.categories.length) {
                data.categories.forEach(category => {
                    category.packs.forEach(pack => {
                        if (pack.highlighted) {
                            pack.categoryId = category.order;
                            highlightedOffers.push(pack);
                        }
                    });
                });
                highlightedOffers.sort((a, b) => a.promoBannerOrder - b.promoBannerOrder);

                if (data.template) {
                    dispatch(setPaymentProvider(data.template));
                }
                setAllOffers(data.categories);
                setHighlitedOffers(highlightedOffers);
                updateOffersCategories(data.categories);
            } else {
                const data = {};
                data.categories = [];
                setAllOffers(data.categories);
                setHighlitedOffers(highlightedOffers);
                updateOffersCategories(data.categories);
            }
    }

    const selectOfferById = (id, categoryOrder) => {
        const category = allOffers.find(category => category.order === categoryOrder);
        const currentlySelectedOffer = category['packs'].find(pack => pack.packId === id);
        dispatch(setSelectedOffer(currentlySelectedOffer));
    }

    // a trick that will make a request every time you focused the tab
    // (this is a FIX for when the browser marks the tab as inactive and after that you go back to it)
    const getIsTabFocused = (event) => {
        // if the refreshAfter timestamp is somewhere in the future, so the offer is not expired - do not refresh the offers
        if (event.type === 'focus' && timePointToBeRefreshed < Date.now()) {
            getOffers();
        }
    }

    const updateOffersCategories = (categories) => {
        const updatedCategories = [];
        categories.forEach(category => {
            const updatedCategory = {...category};
            updatedCategory.id = category.order;
            updatedCategory.isHidden = false;
            updatedCategories.push(updatedCategory);
        });
        if (categories.length) {
            const hiddenCategories = categories.filter(category => category.isHidden);
            hiddenCategories.forEach(category => {
                const categoryToHide = updatedCategories.find(updatedCategory => updatedCategory.id === category.id);
                categoryToHide.isHidden = true;
            });
        }
        setOffersCategories(updatedCategories);
    }

    const filterCategory = (id) => {
        const filteredCategories = [...offersCategories];
        const currentCategory = filteredCategories.find(category => category.id === id);
        const activeCategories = filteredCategories.filter(category => !category.isHidden);
        if (activeCategories.length === 1 && activeCategories[0].id === id) {
            const payload = {
                message: config.filterErr,
                isFilterError: true
            }
            dispatch(showGeneralError(payload));
            return;
        }
        currentCategory.isHidden = !currentCategory.isHidden;
        setOffersCategories(filteredCategories);
    }

    const updateCategoriesStatus = () => {
        const updatedOffers = [...offersCategories];

        updatedOffers.forEach(offersCategory => {
            const currentCategory = offersCategories.find(category => category.id === offersCategory.order);
            offersCategory.isHidden = currentCategory.isHidden;
        });

        if (hasActiveSearch) {
            setSearchedOffers(updatedOffers);
        } else {
            setAllOffers(updatedOffers);
        }
    }

    const openOfferPopupInfo = (info) => {
        // do something if needed and set the info
        setOfferPopupInfo(info);
    }

    const closeOfferPopupInfo = () => {
        // do something if needed and set the info to none
        setOfferPopupInfo({});
    }

    const resetFilter = () => {
        let filterCategories = [...offersCategories];
        filterCategories = filterCategories.map(category => {
            category.isHidden = false;
            return category;
        });
        setOffersCategories(filterCategories);
        getOffers();
    }

    const filterOffers = (text) => {
        const filteredOffers = [];
        allOffers.forEach(category => {
            if (category.name.includes(text)) {
                filteredOffers.push(category);
            } else {
                const updatedCategoryPacks = category.packs.filter(pack => pack.name.includes(text) || pack.price == text);
                if (updatedCategoryPacks.length) {
                    category.packs = updatedCategoryPacks
                    filteredOffers.push(category);
                }
            }
        });

        updateOffersCategories(filteredOffers);
        setHasActiveSearch(true);
        setSearchedOffers(filteredOffers);
    }

    const resetOffersFilter = () => {
        setHasActiveSearch(false);
        setSearchedOffers([]);
        getOffers();
    }

    useEffect(() => {
        let player = localStorage.getItem('shishiFreeGiftPlayerInfo');
        player = JSON.parse(player);
        const pId = searchParams.get('pid');
        const pName = decodeURIComponent(searchParams.get('pname'));

        if ((pId && pId.length)
            && (pName && pName.length)
        ) {
            if (player && player.username && player.userId) {
                logout(pId, pName);
            } else {
                navigate(`/?pid=${pId}&pname=${pName}`);
            }
        }

    }, [searchParams]);

    useEffect(() => {
        if (!player) {
            dispatch(logUserOut());
            return;
        }
        dispatch(startLoading());
        dispatch(clearPurchaseIntention());
        clearTimeout(debounceTimeoutFirstRender.current);
        debounceTimeoutFirstRender.current = setTimeout(() => {
            getOffers();
        }, 300);
        getPromoBannerUrl();

        return () => {
            clearTimeout(debounceTimeout.current);
       };
    }, []);

    useEffect(() => {
        window.addEventListener('focus', getIsTabFocused);

        return () => {
            window.removeEventListener('focus', getIsTabFocused);
        }
    }, [getIsTabFocused]);

    useEffect(() => {
        if (offersCategories && offersCategories.length) {
            updateCategoriesStatus();
        }
    }, [offersCategories]);

    useEffect(() => {
        if (allOffers.length) {
            scheduleRefreshOffers(allOffers);
        }
    }, [allOffers]);

    useEffect(() => {
        if (isFirstPageView.current) {
            window.gtag('event', 'screen_view', {
                'app_name': 'Shishi shop',
                'screen_name': 'page_view_products'
            });
            window.gtag('event', 'page_view', {
                'app_name': 'Shishi shop',
                'page_title': 'shop'
            });
            isFirstPageView.current = false;
        }
    }, []);

    return (
        <div className={styles.wrapper}>
            {selectedOffer ?
                <ShopReviewOrder
                    {...selectedOffer}
                    config={config}
                    windowSize={windowSize}
                />
                :
                <ShopOffers
                    allOffers={allOffers}
                    selectOfferById={selectOfferById}
                    getOffers={getOffers}
                    config={config}
                    windowSize={windowSize}
                    offersCategories={offersCategories}
                    filterCategory={filterCategory}
                    openOfferPopupInfo={openOfferPopupInfo}
                    filterOffers={filterOffers}
                    resetOffersFilter={resetOffersFilter}
                    searchedOffers={searchedOffers}
                    hasActiveSearch={hasActiveSearch}
                    highlightedOffers={highlitedOffers}
                    promoBannerUrl={promoBannerUrl}
                />
            }
            {isLoading ? <Loading withOverlay={true} /> : null}
            {error.hasError ? <PopupInfo text={error.message} action={error.isFilterError ? resetFilter : null} /> : null}
            {Object.keys(offerPopupInfo).length ? <OfferPopupInfo info={offerPopupInfo} closeOfferPopupInfo={closeOfferPopupInfo} selectOfferById={selectOfferById} /> : null}
        </div>
    );
};

export default withRequestHandler(Shop);